import React, { useCallback, useMemo, useState } from "react";
import { ChevronRight, Replay, SaveAlt, TouchApp } from "@mui/icons-material";
import { Box, Breadcrumbs, IconButton, Link, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import { AgChartOptions, AgDonutSeriesOptions, AgNodeClickEvent } from "ag-charts-community";
import { AgCharts as AgChartsComponent } from "ag-charts-react";
import chroma from "chroma-js";
import { chain, sumBy } from "lodash";

import { useZoomableChartRef } from "fond/hooks/useZoomableChartRef";
import { createCostTooltipRenderer } from "fond/utils/agCharts";
import { formatCurrency } from "fond/utils/currency";
import { formatFractionPercent } from "fond/utils/number";
import { ZoomableGridCard } from "fond/widgets/ZoomableGridCard";

import { COST_CHART_HEIGHT, MAX_DRILL_DEPTH } from "../constants";
import { BomTreeRowData } from "../index";

import {
  ChartCenterOverlay,
  ChartContainer,
  ChartSquare,
  FooterHint,
  LegendHeaderRow,
  LegendRow,
  LegendRows,
  LegendSwatch,
  LegendTable,
} from "./BomCostChart.styles";

const LEVEL_KEYS = ["tag", "group", "name"] as const;
const LEVEL_LABELS = ["Tag", "Group", "Item"] as const;

type ChartDatum = { Key: string; TotalCost: number };

interface NavigationItem {
  label: string;
  depth: number;
  filters: Record<string, string>;
}

interface BomCostChartProps {
  rows: BomTreeRowData[];
  totalCost: number;
}

const groupRowsByKey = (key: (typeof LEVEL_KEYS)[number], rows: BomTreeRowData[]): ChartDatum[] =>
  chain(rows)
    .groupBy(key)
    .map((values, id) => ({ Key: id, TotalCost: sumBy(values, "total") }))
    .value();

const BomCostChart: React.FC<BomCostChartProps> = ({ rows, totalCost }: BomCostChartProps) => {
  const { chartRef, downloadChart, onOpenZoom, onCloseZoom } = useZoomableChartRef();
  const [depth, setDepth] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [navigation, setNavigation] = useState<NavigationItem[]>([{ label: "All costs", depth: 0, filters: {} }]);

  const filteredRows = useMemo(() => rows.filter((row) => Object.entries(filters).every(([key, value]) => row[key] === value)), [rows, filters]);

  const chartData = useMemo(() => groupRowsByKey(LEVEL_KEYS[depth], filteredRows), [filteredRows, depth]);
  const chartTotal = useMemo(() => sumBy(chartData, "TotalCost"), [chartData]);

  const onDrillDown = useCallback(
    (params: AgNodeClickEvent<"seriesNodeClick", ChartDatum>) => {
      if (depth >= MAX_DRILL_DEPTH) return;

      const key = params.datum.Key;
      const newFilters = { ...filters, [LEVEL_KEYS[depth]]: key };
      const newDepth = depth + 1;

      setFilters(newFilters);
      setDepth(newDepth);
      setNavigation((prev) => [...prev, { label: key, depth: newDepth, filters: newFilters }]);
    },
    [depth, filters]
  );

  const navigateBackTo = (index: number) => {
    const target = navigation[index];
    setNavigation((prev) => prev.slice(0, index + 1));
    setDepth(target.depth);
    setFilters(target.filters);
  };

  const getTooltip = useMemo(() => createCostTooltipRenderer<ChartDatum>(totalCost, { heading: "Total cost" }), [totalCost]);

  const colors = useMemo(() => chroma.scale([blue[100], blue[800]]).colors(Math.max(chartData.length, 1)), [chartData.length]);

  const chartSeries: AgDonutSeriesOptions[] = useMemo(
    (): AgDonutSeriesOptions[] => [
      {
        angleKey: "TotalCost",
        calloutLabel: { enabled: false },
        fills: colors,
        innerRadiusRatio: 0.65,
        listeners: {
          seriesNodeClick: onDrillDown,
        },
        sectorLabel: { enabled: false },
        sectorLabelKey: "Key",
        type: "donut",
        tooltip: {
          renderer: getTooltip,
        },
      },
    ],
    [colors, onDrillDown, getTooltip]
  );

  const options: AgChartOptions = useMemo(
    () => ({
      data: chartData,
      series: chartSeries,
      // We render our own legend table beside the chart, so disable ag-charts' auto-generated legend
      legend: { enabled: false },
    }),
    [chartData, chartSeries]
  );

  const currentLevelLabel = LEVEL_LABELS[depth];
  const nextLevelLabel = depth < MAX_DRILL_DEPTH ? LEVEL_LABELS[depth + 1] : null;
  const overallPercentage = formatFractionPercent(totalCost > 0 ? chartTotal / totalCost : 0);

  return (
    <ZoomableGridCard
      breakpoints={{ lg: 6, xs: 12 }}
      sx={{ height: COST_CHART_HEIGHT }}
      title="Cost breakdown"
      headerRightElement={
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Grouped by
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={depth}
              onChange={(_event, value) => {
                if (value === null) return;
                const navigationIndex = navigation.findIndex((item) => item.depth === value);
                if (navigationIndex !== -1) navigateBackTo(navigationIndex);
              }}
            >
              {LEVEL_LABELS.map((label, index) => (
                <ToggleButton key={label} value={index} disabled={!navigation.some((item) => item.depth === index)}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <IconButton data-testid="download-cost-breakdown-button" size="small" onClick={downloadChart}>
            <SaveAlt fontSize="inherit" />
          </IconButton>
        </Box>
      }
      onOpen={onOpenZoom}
      onClose={onCloseZoom}
    >
      <Box display="flex" flexDirection="column" flex={1} minHeight={0} p={2.5} gap={2}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton size="small" onClick={() => navigateBackTo(0)} disabled={navigation.length === 1}>
            <Replay fontSize="inherit" />
          </IconButton>
          <Breadcrumbs>
            {navigation.map((item, index) => (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid, react/no-array-index-key
              <Link component="button" color="inherit" key={`${item.label}-${index}`} underline="hover" onClick={() => navigateBackTo(index)}>
                {item.label}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>
        <Box display="flex" flex={1} minHeight={0} gap={3}>
          <Box py={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <ChartContainer>
              <ChartSquare>
                <AgChartsComponent ref={chartRef} options={options} style={{ height: "100%", width: "100%" }} />
                <ChartCenterOverlay>
                  <Typography variant="body3" color="text.secondary" fontWeight={700} letterSpacing={0.5}>
                    TOTAL COST
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {formatCurrency(chartTotal, { notation: "compact" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {overallPercentage} of total
                  </Typography>
                </ChartCenterOverlay>
              </ChartSquare>
            </ChartContainer>
            <FooterHint sx={{ mb: 1, visibility: nextLevelLabel ? "visible" : "hidden" }}>
              <TouchApp fontSize="inherit" />
              {nextLevelLabel ? `Click a segment to drill into ${nextLevelLabel.toLowerCase()}s` : null}
            </FooterHint>
          </Box>
          <LegendTable>
            <LegendHeaderRow>
              <Box flex={1}>{currentLevelLabel}</Box>
              <Box width={110} textAlign="right">
                Total cost
              </Box>
              <Box width={100} textAlign="right">
                % of group
              </Box>
              <Box width={24} />
            </LegendHeaderRow>
            <LegendRows className="customScrollbars">
              {chartData.map((datum, index) => {
                const clickable = depth < MAX_DRILL_DEPTH;
                return (
                  <LegendRow
                    key={datum.Key}
                    clickable={clickable}
                    onClick={() => clickable && onDrillDown({ datum } as AgNodeClickEvent<"seriesNodeClick", ChartDatum>)}
                  >
                    <Box flex={1} display="flex" alignItems="center" gap={1} minWidth={0}>
                      <LegendSwatch sx={{ backgroundColor: colors[index] }} />
                      <Typography variant="body3" noWrap>
                        {datum.Key}
                      </Typography>
                    </Box>
                    <Box width={110} textAlign="right">
                      <Typography variant="body3" fontWeight={700}>
                        {formatCurrency(datum.TotalCost, { notation: "compact" })}
                      </Typography>
                    </Box>
                    <Box width={100} textAlign="right">
                      <Typography variant="body3" color="text.secondary">
                        {formatFractionPercent(chartTotal > 0 ? datum.TotalCost / chartTotal : 0)}
                      </Typography>
                    </Box>
                    <Box width={24} display="flex" justifyContent="flex-end">
                      {clickable && <ChevronRight fontSize="small" color="disabled" />}
                    </Box>
                  </LegendRow>
                );
              })}
            </LegendRows>
          </LegendTable>
        </Box>
      </Box>
    </ZoomableGridCard>
  );
};

export default BomCostChart;
